import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { Router } from "@angular/router";
import { driver, Driver } from "driver.js";
import type { DriveStep } from "driver.js";
import type { TourDefinition, TourId } from "./tours.types";
import { TOURS_CONFIG } from "./tours.config";
import { UsuariosService } from "../services/usuarios.service";
import { k } from "src/environments/properties";
import { id } from "@swimlane/ngx-charts";
import { firstValueFrom } from "rxjs";

@Injectable({ providedIn: "root" })
export class TourEngineService {
  private d?: Driver;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private usuarioService: UsuariosService,
  ) {}

  // ---------- Helpers ----------

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private storageKey(id: TourId): string {
    return `tour_seen_${id}`;
  }

  seen(id: TourId): boolean {
    if (!this.isBrowser()) return false;

    return localStorage.getItem(this.storageKey(id)) === "1";
  }

  markSeen(id: TourId): void {
    if (!this.isBrowser()) return;

    localStorage.setItem(this.storageKey(id), "1");
  }

  stop(): void {
    this.d?.destroy();
    this.d = undefined;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private waitForSelector(
    selector: string,
    timeoutMs = 7000,
  ): Promise<Element> {
    return new Promise((resolve, reject) => {
      const start = Date.now();

      const tick = () => {
        const el = document.querySelector(selector);

        if (el) return resolve(el);

        if (Date.now() - start > timeoutMs) {
          return reject(new Error(`No se encontró el elemento: ${selector}`));
        }

        requestAnimationFrame(tick);
      };

      tick();
    });
  }

  /**
   * Crea la instancia de Driver
   */
  private createDriver(opts?: { onDestroyed?: () => void }): Driver {
    return driver({
      animate: true,

      showProgress: true,

      allowClose: true,

      stagePadding: 8,

      stageRadius: 10,

      nextBtnText: "Siguiente",

      prevBtnText: "Anterior",

      doneBtnText: "Finalizar",

      /**
       * Agregamos nuestro botón personalizado
       */
      onPopoverRender: (popover) => {
        const button = document.createElement("button");

        button.type = "button";

        button.innerText = "No mostrar más";

        button.className = "tour-no-more-button";

        button.addEventListener("click", async () => {
          // Deshabilitamos las visitas guiadas
          TOURS_CONFIG.enabled = false;
          let userId = localStorage.getItem(k.user_id) ?? "";
          const response = await firstValueFrom(
            this.usuarioService.updateUsuario(userId, {
              accesoGuiado: false,
            }),
          );

          // Cerramos el tour
          this.stop();
        });

        // Agregar botón al footer
        popover.footer.appendChild(button);
      },

      onDestroyed: () => {
        opts?.onDestroyed?.();
      },
    } as any);
  }

  // ---------- Public API ----------

  async start(
    def: TourDefinition,
    options?: { force?: boolean },
  ): Promise<void> {
    if (!this.isBrowser()) return;

    const force = options?.force ?? false;

    const remember = def.remember ?? true;

    if (remember && !force && this.seen(def.id)) {
      return;
    }

    // Navega a la ruta si aplica
    if (def.route) {
      await this.router.navigateByUrl(def.route);

      await this.sleep(50);
    }

    // Espera el primer elemento
    const firstSelector = def.steps.find((s) => typeof s.element === "string")
      ?.element as string | undefined;

    if (firstSelector) {
      try {
        await this.waitForSelector(firstSelector);
      } catch {
        // No bloqueamos el tour
      }
    }

    this.d = this.createDriver({
      onDestroyed: () => {
        if (remember) {
          this.markSeen(def.id);
        }
      },
    });

    this.d.setSteps(def.steps as DriveStep[]);

    this.d.drive();
  }
}
